// C:\Users\User\Desktop\Bresco\bresco-franquicies-frontend\src\components\ui\Table\TableActionBar.js
import { useModal } from 'contexts/ModalContext'
import { useUser } from 'contexts/UserContext'
import { saveAs } from 'file-saver'
import JsPDF from 'jspdf'
import 'jspdf-autotable'
import PropTypes from 'prop-types'
import { useLocation } from 'wouter'
import * as XLSX from 'xlsx-js-style'
import { LuDownload, LuFilterX, LuPencil, LuUpload, LuFileSpreadsheet } from 'react-icons/lu'

// ✅ Mapeado completo de campos del backend -> nombre legible para export
// Solo se usa cuando se pasa rawData (datos completos del backend)
const FULL_EXPORT_COLUMNS = [
  { key: 'IDInterno',              label: 'ID Interno' },
  { key: 'ref_albaran',            label: 'Ref. Albarán' },
  { key: 'estado_siniestro',       label: 'Estado Siniestro' },
  // { key: 'estado_bresco',          label: 'Estado Bresco' },
  { key: 'created_at',             label: 'Fecha Alta',             isDate: true },
  { key: 'fecha_dia',              label: 'Fecha Día',              isDate: true },
  { key: 'fecha_siniestro',        label: 'Fecha Siniestro',        isDate: true },
  { key: 'causa',                  label: 'Causa' },
  { key: 'delegacion_propietaria', label: 'Delegación Propietaria' },
  { key: 'nombre_franquiciado',    label: 'Franquiciado' },
  { key: 'ref_fast',               label: 'Ref. FAST' },
  { key: 'tipo_pago',              label: 'Tipo Pago' },
  { key: 'importe_reclamado',      label: 'Importe Reclamado',      isAmount: true, isCurrency: true },
  { key: 'franquicia_aplicable',   label: 'Franquicia Aplicable',      isAmount: true, isCurrency: true },
  { key: 'importe_neto_cobrar',    label: 'Importe Neto a Cobrar',      isAmount: true, isCurrency: true },
  { key: 'descripcion_mercancia',  label: 'Descripción Mercancía' },
  { key: 'peso_mercancia_kg',      label: 'Peso Mercancía (kg)',      isAmount: true },
  { key: 'margen_porcentaje',      label: 'Margen (%)',      isAmount: true },
  { key: 'cuenta',                 label: 'Cuenta (IBAN)' },
  { key: 'concepto_factura',       label: 'Concepto Factura' },
  { key: 'comentarios',            label: 'Comentarios' },
  { key: 'reclamacion',            label: 'Reclamación' },
  { key: 'parte_extravio',         label: 'Parte Extravío' },
  { key: 'albaran',                label: 'Albarán' },
  { key: 'factura_compra',         label: 'Factura Compra' },
  { key: 'denuncia',               label: 'Denuncia' },
  { key: 'factura_venta',          label: 'Factura Venta' },
  { key: 'expediente_completo',    label: 'Expediente Completo' },
  // { key: 'updated_by_full_name',   label: 'Última modificación por' },
  // { key: 'updated_at',             label: 'Fecha última modificación', isDate: true },
  // { key: 'created_at',             label: 'Fecha creación',            isDate: true },
]

const MRW_VARIANTES = {
  SO:   { poliza: 'ESCGN245001' },
  YTLL: { poliza: 'ESCGN245000' },
}
const getMRWPoliza = (tipoPago) => {
  const key = String(tipoPago || '').trim().toUpperCase()
  return MRW_VARIANTES[key]?.poliza ?? 'ESCGN244946'
}
// Devuelve un número real (no texto) para que Excel pueda sumar las cantidades.
// El símbolo de € lo pone el formato de celda, no el valor. Ver aplicación de
// numFmt sobre las columnas de importe más abajo.
const parseAmountMRW = (value) => {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(String(value).replace(',', '.'))
  if (!Number.isFinite(n)) return ''
  return n
}
// Formato "moneda" de Excel (locale ES): número con 2 decimales y símbolo €.
const MRW_EUR_FMT = '#,##0.00\\ €'
const formatKgMRW = (value) => {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(String(value).replace(',', '.'))
  if (!Number.isFinite(n)) return ''
  return n.toFixed(2)
}

/**
 * Formatea un ISO string o DATETIME a "DD/MM/YYYY" (sin hora).
 * Igual que el formatDateOnly usado en la tabla.
 */
const formatDateOnly = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d.getTime())) return String(value)
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`
}

const toExcelDate = (value) => {
  if (!value) return ''
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return ''
  // En UTC para que el horario de verano no reste un día (la web muestra la fecha literal)
  const t = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  if (isNaN(t)) return ''
  return Math.round((t - Date.UTC(1899, 11, 30)) / 86400000)
}

/**
 * Obtiene el valor de un campo para export, aplicando formato de fecha si corresponde.
 */
// const getRawExportValue = (item, col) => {
//   const v = item[col.key]
//   if (v === null || v === undefined) return ''
//   if (col.isDate) return formatDateOnly(v)
//   return String(v)
// }
const getRawExportValue = (item, col) => {
  const v = item[col.key]
  if (v === null || v === undefined) return ''
  if (col.isDate) return toExcelDate(v)
  if (col.isAmount) {
    const n = Number(String(v).replace(',', '.'))
    if (!Number.isFinite(n)) return String(v)
    // Número real (no texto) para que Excel permita ordenar/filtrar por importe.
    // La coma decimal la pone Excel según la configuración regional (es-ES).
    return n
  }
  return String(v)
}

// ── Mapea el estado de filtros de react-table a query string para los endpoints de export ──
const EXPORT_FILTER_MAP = {
  Siniestros_Estado:            { type: 'array',     key: 'estado' },
  Siniestros_Causa:             { type: 'array',     key: 'causa' },
  Siniestros_Tipo_Pago:         { type: 'array',     key: 'tipo_pago' },
  Siniestros_Delegacion:        { type: 'text',      key: 'delegacion' },
  Siniestros_Franquiciado:      { type: 'text',      key: 'franquiciado' },
  Siniestros_Ref_Albaran:       { type: 'text',      key: 'ref_albaran' },
  Siniestros_Ref_Fast:          { type: 'text',      key: 'ref_fast' },
  Siniestros_IDInterno:         { type: 'text',      key: 'id_interno' },
  Siniestros_Comentarios:       { type: 'text',      key: 'comentarios' },
  Siniestros_Finiquito_Enviado: { type: 'finiquito', key: 'finiquito' },
  Siniestros_Created_At:        { type: 'dateRange', keyDesde: 'fecha_alta_desde',      keyHasta: 'fecha_alta_hasta' },
  Siniestros_Fecha_Dia:         { type: 'dateRange', keyDesde: 'fecha_dia_desde',        keyHasta: 'fecha_dia_hasta' },
  Siniestros_Fecha_Siniestro:   { type: 'dateRange', keyDesde: 'fecha_siniestro_desde',  keyHasta: 'fecha_siniestro_hasta' },
  // Siniestros_ModificadoPor: no tiene soporte server-side (campo calculado de JOIN)
}

function buildExportQueryParams(filterState = [], extraParams = {}) {
  const params = new URLSearchParams()

  // Estados archivados cargados
  const archivedEstados = Array.isArray(extraParams?.archivedEstados) ? extraParams.archivedEstados : []
  archivedEstados.forEach((e) => params.append('estados[]', e))

  for (const { id, value } of filterState) {
    const mapping = EXPORT_FILTER_MAP[id]
    if (!mapping || value === undefined || value === null) continue

    switch (mapping.type) {
      case 'array': {
        const arr = Array.isArray(value) ? value : [value]
        arr.filter(Boolean).forEach((v) => params.append(`${mapping.key}[]`, v))
        break
      }
      case 'text': {
        if (value) params.set(mapping.key, value)
        break
      }
      case 'finiquito': {
        if (value && value !== 'TODOS') params.set(mapping.key, value)
        break
      }
      case 'dateRange': {
        if (Array.isArray(value)) {
          if (value[0]) params.set(mapping.keyDesde, value[0])
          if (value[1]) params.set(mapping.keyHasta, value[1])
        }
        break
      }
      default: break
    }
  }

  return params.toString()
}

export default function TableActionBar({
  tableName,
  createNewItemURL,
  datos,
  rawData,
  rows,
  page,
  columns,
  setAllFilters,
  allowImportExcel,
  uploadExcelURL,
  handleRefresh,
  handleAnalyzeExcel,
  exportMRWUrl,
  filterState,
  exportExtraParams
}) {
  const [, setLocation] = useLocation()
  const { user } = useUser()
  const { openModal } = useModal()

  const buildTimestamp = () => {
    const d = new Date()
    return (
      d.getFullYear().toString() +
      (d.getMonth() + 1).toString().padStart(2, '0') +
      d.getDate().toString().padStart(2, '0') +
      d.getHours().toString().padStart(2, '0') +
      d.getMinutes().toString().padStart(2, '0') +
      d.getSeconds().toString().padStart(2, '0')
    )
  }

  const getExportableColumns = (type /* 'excel' | 'pdf' */) => {
    const flag = type === 'excel' ? 'showInExcel' : 'showInPDF'
    return (columns || [])
      .filter((c) => c && c.accessor)
      .filter((c) => c[flag] !== false)
  }

  // Filtradas + ordenadas (todas las páginas)
  const getExportRows = () => rows || []

  // ✅ Aplica exportFormat si existe, para que salga igual que en UI
  const getCellExportValue = (row, col) => {
    const raw = row?.values?.[col.accessor]
    if (typeof col.exportFormat === 'function') {
      try {
        return col.exportFormat(raw, row?.original, row)
      } catch (e) {
        // Si falla el formatter, devolvemos raw para no romper export
        return raw ?? ''
      }
    }
    return raw ?? ''
  }

  const handleExcel = async () => {
    const apiBase = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || ''
    const token = user?.accessToken

    // Si hay endpoint dedicado (tablas con exportExtraParams o filterState), usarlo
    if (filterState !== undefined) {
      try {
        const qs = buildExportQueryParams(filterState || [], exportExtraParams)
        const url = `${apiBase}/siniestros/export/excel${qs ? `?${qs}` : ''}`
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        if (!res.ok) throw new Error(`Error ${res.status} exportando Excel`)
        const exportData = await res.json()
        if (!Array.isArray(exportData) || exportData.length === 0) return

        const sheetData = exportData.map((item) => {
          const obj = {}
          FULL_EXPORT_COLUMNS.forEach((col) => { obj[col.label] = getRawExportValue(item, col) })
          return obj
        })

        const worksheet = XLSX.utils.json_to_sheet(sheetData)
        worksheet['!cols'] = FULL_EXPORT_COLUMNS.map(() => ({ wch: 22 }))
        // Autofiltro en la cabecera para que se pueda filtrar/ordenar nada más abrir
        worksheet['!autofilter'] = { ref: worksheet['!ref'] }

        const dateColIndexes = FULL_EXPORT_COLUMNS.map((col, i) => (col.isDate ? i : null)).filter((i) => i !== null)
        const rowCount = sheetData.length
        dateColIndexes.forEach((colIdx) => {
          for (let rowIdx = 1; rowIdx <= rowCount; rowIdx++) {
            const cellRef = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx })
            const cell = worksheet[cellRef]
            if (cell && typeof cell.v === 'number') { cell.t = 'n'; cell.z = 'DD/MM/YYYY'; cell.s = { numFmt: 'DD/MM/YYYY' } }
          }
        })

        const currencyColIndexes = FULL_EXPORT_COLUMNS.map((col, i) => (col.isCurrency ? i : null)).filter((i) => i !== null)
        currencyColIndexes.forEach((colIdx) => {
          for (let rowIdx = 1; rowIdx <= rowCount; rowIdx++) {
            const cellRef = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx })
            const cell = worksheet[cellRef]
            if (cell && typeof cell.v === 'number') { cell.t = 'n'; cell.z = MRW_EUR_FMT; cell.s = { numFmt: MRW_EUR_FMT } }
          }
        })

        const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] }
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
        saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), `${tableName}_${buildTimestamp()}.xlsx`)
      } catch (err) {
        console.error('Export Excel error:', err)
        alert(err?.message || 'Error generando el Excel')
      }
      return
    }

    // Fallback: exportar solo columnas visibles (otras tablas sin endpoint dedicado)
    const exportCols = getExportableColumns('excel')
    const exportRows = getExportRows()
    const exportData = exportRows.map((r) => {
      const obj = {}
      exportCols.forEach((col) => { obj[col.accessor] = getCellExportValue(r, col) })
      return obj
    })
    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const headers = exportCols.map((col) => col.Header || col.accessor)
    worksheet['!cols'] = headers.map(() => ({ wch: 20 }))
    headers.forEach((header, index) => {
      const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: index })]
      if (cell) cell.v = header
    })
    const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] }
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), `${tableName}_${buildTimestamp()}.xlsx`)
  }

  const handlePDF = async () => {
    const C_DARK  = [23, 73, 94]     // #17495E
    const C_AMBER = [252, 174, 82]   // #FCAE52
    const C_LIGHT = [248, 249, 250]
    const C_WHITE = [255, 255, 255]
    const C_GRAY  = [100, 100, 100]

    const buildPDFBody = (data) =>
      data.map((item) =>
        FULL_EXPORT_COLUMNS.map((col) => {
          const v = item[col.key]
          if (v === null || v === undefined) return ''
          if (col.isDate) return formatDateOnly(v)
          if (col.isAmount) {
            const n = Number(String(v).replace(',', '.'))
            return Number.isFinite(n) ? String(n).replace('.', ',') : String(v)
          }
          return String(v)
        })
      )

    const renderDoc = (doc, head, body, total) => {
      const pageW = doc.internal.pageSize.getWidth()
      const pageH = doc.internal.pageSize.getHeight()

      // ── Cabecera ──────────────────────────────────────────
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(13)
      doc.setTextColor(...C_DARK)
      doc.text(tableName, 14, 13)

      doc.setFont('helvetica', 'normal')
      doc.setFontSize(7.5)
      doc.setTextColor(...C_GRAY)
      doc.text(
        `Exportado el ${new Date().toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}  ·  ${total} registros`,
        14, 19
      )

      // Línea ámbar bajo la cabecera
      doc.setDrawColor(...C_AMBER)
      doc.setLineWidth(0.6)
      doc.line(14, 22, pageW - 14, 22)

      // ── Tabla ─────────────────────────────────────────────
      doc.autoTable({
        head: [head],
        body,
        startY: 26,
        styles: {
          fontSize: 5.5,
          cellPadding: 1.2,
          lineWidth: 0.05,
          lineColor: [220, 220, 220],
          textColor: [40, 40, 40],
          font: 'helvetica',
          overflow: 'ellipsize',
        },
        margin: { top: 28, right: 5, bottom: 14, left: 5 },
        pageBreak: 'auto',
        rowPageBreak: 'avoid',
        headStyles: {
          fillColor: C_DARK,
          textColor: C_WHITE,
          fontStyle: 'bold',
          fontSize: 5.5,
          lineWidth: 0,
          cellPadding: 1.5,
        },
        alternateRowStyles: { fillColor: C_LIGHT },
        bodyStyles: { fillColor: C_WHITE },
        showHead: 'everyPage',
        didDrawPage: ({ pageNumber }) => {
          // Footer con número de página
          doc.setFontSize(6.5)
          doc.setTextColor(...C_GRAY)
          doc.text(
            `Página ${pageNumber}`,
            pageW / 2,
            pageH - 6,
            { align: 'center' }
          )
          // Línea ámbar en el footer
          doc.setDrawColor(...C_AMBER)
          doc.setLineWidth(0.4)
          doc.line(14, pageH - 9, pageW - 14, pageH - 9)
        },
      })

      doc.save(`${tableName}_${buildTimestamp()}.pdf`)
    }

    const apiBase = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || ''
    const token = user?.accessToken

    // Ruta principal: endpoint dedicado con query params
    if (filterState !== undefined) {
      try {
        const qs = buildExportQueryParams(filterState || [], exportExtraParams)
        const url = `${apiBase}/siniestros/export/excel${qs ? `?${qs}` : ''}`
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        if (!res.ok) throw new Error(`Error ${res.status} exportando PDF`)
        const exportData = await res.json()
        if (!Array.isArray(exportData) || exportData.length === 0) return

        const doc = new JsPDF({ orientation: 'landscape' })
        const head = FULL_EXPORT_COLUMNS.map((c) => c.label)
        renderDoc(doc, head, buildPDFBody(exportData), exportData.length)
      } catch (err) {
        console.error('Export PDF error:', err)
        alert(err?.message || 'Error generando el PDF')
      }
      return
    }

    // Fallback: columnas visibles (otras tablas sin endpoint dedicado)
    const doc = new JsPDF({ orientation: 'landscape' })
    const exportCols = getExportableColumns('pdf')
    const exportRows = getExportRows()
    const head = exportCols.map((col) => col.Header || col.accessor)
    const body = exportRows.map((row) => exportCols.map((col) => getCellExportValue(row, col)))
    const pageW = doc.internal.pageSize.getWidth()
    const pageH = doc.internal.pageSize.getHeight()

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(13)
    doc.setTextColor(...C_DARK)
    doc.text(tableName, 14, 13)
    doc.setDrawColor(...C_AMBER)
    doc.setLineWidth(0.6)
    doc.line(14, 17, pageW - 14, 17)

    doc.autoTable({
      head: [head],
      body,
      startY: 21,
      styles: { fontSize: 7, cellPadding: 1.5, lineWidth: 0.05, lineColor: [220, 220, 220], textColor: [40, 40, 40] },
      margin: { top: 24, right: 10, bottom: 14, left: 10 },
      pageBreak: 'auto',
      rowPageBreak: 'avoid',
      headStyles: { fillColor: C_DARK, textColor: C_WHITE, fontStyle: 'bold', lineWidth: 0 },
      alternateRowStyles: { fillColor: C_LIGHT },
      bodyStyles: { fillColor: C_WHITE },
      showHead: 'everyPage',
      didDrawPage: ({ pageNumber }) => {
        doc.setFontSize(6.5)
        doc.setTextColor(...C_GRAY)
        doc.text(`Página ${pageNumber}`, pageW / 2, pageH - 6, { align: 'center' })
        doc.setDrawColor(...C_AMBER)
        doc.setLineWidth(0.4)
        doc.line(14, pageH - 9, pageW - 14, pageH - 9)
      },
    })
    doc.save(`${tableName}_${buildTimestamp()}.pdf`)
  }
    const handleExportMRW = async () => {
    if (!exportMRWUrl) return
    try {
      const apiBase = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || ''
      const token = user?.accessToken
      const qs = buildExportQueryParams(filterState || [], exportExtraParams)
      const url = `${apiBase}/${exportMRWUrl}${qs ? `?${qs}` : ''}`

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) throw new Error(`Error ${res.status} obteniendo datos MRW`)
      const mrwData = await res.json()
      const MRW_COLUMNS = [
        'NÚMERO DE PÓLIZA',
        'REF. BRESCO & ALARI',
        'ID',
        'REF. ALBARAN',
        'FECHA SINIESTRO',
        'CAUSA',
        'IMPORTE RECLAMADO',
        'FRANQUÍCIA APLICABLE',
        'IMPORTE NETO A COBRAR',
        'TIPO PAGO',
        'RESPONSABLE 1',
        'RESPONSABLE 2',
        'RESPONSABLE 3',
        'DELEGACIÓN PROPIETARIA',
        'Nombre del franquiciado',
        'Descripción mercancía',
        'Peso mercancía (Kg)',
        'CUENTA',
        'SITUACIÓN DEL SINIESTRO',
        'FECHA CIERRE B&A',
        'FECHA TRANSFERENCIA PAGO CHUBB',
        'RESOLUCIÓN DEL SINIESTRO',
        'REEMBOLSO ( MAX - 600 )',
      ]
      const exportRows = mrwData.map((item) => ({
        'NÚMERO DE PÓLIZA':               getMRWPoliza(item.tipo_pago),
        'REF. BRESCO & ALARI':            '',
        'ID':                             item.IDInterno ?? '',
        'REF. ALBARAN':                   item.ref_albaran ?? '',
        'FECHA SINIESTRO':                item.fecha_siniestro ? toExcelDate(item.fecha_siniestro) : '',
        'CAUSA':                          item.causa ?? '',
        'IMPORTE RECLAMADO':              parseAmountMRW(item.importe_reclamado),
        'FRANQUÍCIA APLICABLE':           parseAmountMRW(item.franquicia_aplicable),
        'IMPORTE NETO A COBRAR':          parseAmountMRW(item.importe_neto_cobrar),
        'TIPO PAGO':                      item.tipo_pago ?? '',
        'RESPONSABLE 1':                  '',
        'RESPONSABLE 2':                  '',
        'RESPONSABLE 3':                  '',
        'DELEGACIÓN PROPIETARIA':         item.delegacion_propietaria ?? '',
        'Nombre del franquiciado':        item.nombre_franquiciado ?? '',
        'Descripción mercancía':          item.descripcion_mercancia ?? '',
        'Peso mercancía (Kg)':            formatKgMRW(item.peso_mercancia_kg),
        'CUENTA':                         item.cuenta ?? '',
        'SITUACIÓN DEL SINIESTRO':        '',
        'FECHA CIERRE B&A':               '',
        'FECHA TRANSFERENCIA PAGO CHUBB': '',
        'RESOLUCIÓN DEL SINIESTRO':       '',
        'REEMBOLSO ( MAX - 600 )':        '',
      }))
      const worksheet = XLSX.utils.json_to_sheet(exportRows, { header: MRW_COLUMNS })
      worksheet['!cols'] = MRW_COLUMNS.map(() => ({ wch: 24 }))
      const dateColIdx = 4
      // Columnas de importe: IMPORTE RECLAMADO, FRANQUÍCIA APLICABLE, IMPORTE NETO A COBRAR
      const amountColIdxs = [6, 7, 8]
      const rowCount = exportRows.length
      for (let rowIdx = 1; rowIdx <= rowCount; rowIdx++) {
        const cellRef = XLSX.utils.encode_cell({ r: rowIdx, c: dateColIdx })
        const cell = worksheet[cellRef]
        if (cell && typeof cell.v === 'number') {
          cell.t = 'n'
          cell.z = 'DD/MM/YYYY'
          cell.s = { numFmt: 'DD/MM/YYYY' }
        }
        for (const colIdx of amountColIdxs) {
          const amountRef = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx })
          const amountCell = worksheet[amountRef]
          if (amountCell && typeof amountCell.v === 'number') {
            amountCell.t = 'n'
            amountCell.z = MRW_EUR_FMT
            amountCell.s = { numFmt: MRW_EUR_FMT }
          }
        }
      }
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] }
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' })
      saveAs(blob, `Export_MRW_${buildTimestamp()}.xlsx`)
    } catch (err) {
      console.error('Export MRW error:', err)
      alert(err?.message || 'Error generando el Excel MRW')
    }
  }

  return (
    <>
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="flex items-center gap-x-3">
          <h2 className="text-lg font-medium text-gray-800 dark:text-white">{tableName}</h2>
          <span className="px-3 py-1 text-xs text-white bg-[#17495E] rounded-full dark:bg-gray-800 dark:text-blue-400">
            {rows?.length !== datos?.length
              ? `${rows?.length} / ${datos?.length}`
              : rows?.length}
          </span>
        </div>
      </div>
      <div className="flex flex-col-reverse items-center justify-between mt-4 sm:mt-5 sm:flex-row gap-x-4">
        <div className="flex items-center w-full mt-5 sm:w-fit gap-x-4 sm:mt-0">
          {allowImportExcel && (user?.role_id === 1 || user?.role_id === 2) ? (
            <button
              title="Importar Excel"
              className="flex items-center justify-center w-1/2 px-5 py-2 space-x-2 text-sm text-white transition-colors duration-200 bg-[#FCAE52] border border-[#FCAE52] rounded-sm sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-[#FCAE52] dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800"
              onClick={() =>
                openModal('UploadFileModal', {
                  uploadExcelURL,
                  handleRefresh,
                  handleAnalyzeExcel
                })
              }
            >
              <LuUpload size={18} />
              <span>Importar</span>
            </button>
          ) : null}
          <button
            className="flex items-center justify-center w-1/2 px-5 py-2 space-x-2 text-sm text-white transition-colors duration-200 bg-[#FCAE52] border border-[#FCAE52] rounded-sm sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-[#FCAE52] dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30"
            onClick={handleExcel}
            disabled={rows?.length === 0}
          >
            <LuDownload size={18} />
            <span>Excel</span>
          </button>
          <button
            className="flex items-center justify-center w-1/2 px-5 py-2 space-x-2 text-sm text-white transition-colors duration-200 bg-[#FCAE52] border border-[#FCAE52] rounded-sm sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-[#FCAE52] dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30"
            onClick={handlePDF}
            disabled={rows?.length === 0}
          >
            <LuDownload size={18} />
            <span>PDF</span>
          </button>
          {exportMRWUrl && (
            <button
              className="flex items-center justify-center w-1/2 px-5 py-2 space-x-2 text-sm text-white transition-colors duration-200 bg-[#17495E] border border-[#17495E] rounded-sm sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-[#17495E] dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30"
              onClick={handleExportMRW}
              disabled={rows?.length === 0}
            >
              <LuFileSpreadsheet size={18} />
              <span>MRW</span>
            </button>
          )}
        </div>
        <div className="flex items-center w-full sm:w-fit gap-x-4">
          <button
            className="flex items-center justify-center w-1/2 px-5 py-2 space-x-2 text-sm text-white transition-colors duration-200 bg-[#FCAE52] border border-[#FCAE52] rounded-sm sm:w-36 dark:hover:bg-gray-800 dark:bg-gray-900 hover:bg-white hover:text-[#FCAE52] dark:text-white dark:border-gray-700"
            onClick={() => {
              setAllFilters([])
              columns.forEach((column) => localStorage.setItem(column.accessor, undefined))
            }}
          >
            <LuFilterX size={18} />
            <span>Borrar</span>
          </button>
          {user?.role_id !== 3 && (
            <button
              type="button"
              className="flex items-center justify-center w-1/2 px-5 py-2 space-x-2 text-sm text-white transition-colors duration-200 bg-[#FCAE52] border border-[#FCAE52] rounded-sm sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-[#FCAE52] dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800"
              onClick={() => setLocation(createNewItemURL)}
            >
              <LuPencil size={18} />
              <span>Crear</span>
            </button>
          )}
        </div>
      </div>
    </>
  )
}

TableActionBar.propTypes = {
  tableName: PropTypes.string.isRequired,
  createNewItemURL: PropTypes.string.isRequired,
  datos: PropTypes.array,
  rawData: PropTypes.array,
  rows: PropTypes.any,
  page: PropTypes.any,
  columns: PropTypes.any,
  setAllFilters: PropTypes.func,
  allowImportExcel: PropTypes.bool,
  uploadExcelURL: PropTypes.string,
  handleRefresh: PropTypes.func,
  handleAnalyzeExcel: PropTypes.func,
  exportMRWUrl: PropTypes.string,
  filterState: PropTypes.array,
  exportExtraParams: PropTypes.object
}